import React, { useRef, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './WellPath2D.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function WellPath2D({ data }) {
  const [selectedView, setSelectedView] = useState('all'); // all, top, east, north
  
  if (!data || data.length === 0) {
    return <div className="well-path-2d-empty">No deviation data available for plotting.</div>;
  }

  // Sort data by depth
  const sortedData = [...data].sort((a, b) => (parseFloat(a.Depth) || 0) - (parseFloat(b.Depth) || 0));

  // Prepare data points
  const points = sortedData.map((row, index) => {
    // More explicit parsing to avoid the || 0 fallback issues
    const parsedDx = parseFloat(row.DX);
    const parsedDy = parseFloat(row.DY);
    
    const point = {
      depth: parseFloat(row.Depth) || 0,
      dx: isNaN(parsedDx) ? 0 : parsedDx,
      dy: isNaN(parsedDy) ? 0 : parsedDy,
      tvd: parseFloat(row.TVD) || 0,
      inclination: parseFloat(row.Inclination) || 0,
      azimuth: parseFloat(row.Azimuth) || 0
    };
    
    // Debug first few points to see what WellPath2D is actually getting
    if (index < 3) {
      console.log(`📈 WellPath2D processing point ${index + 1}:`, {
        originalRow: row,
        processedPoint: point,
        rawDX: row.DX,
        rawDY: row.DY,
        parsedDX: parsedDx,
        parsedDY: parsedDy,
        isNaNDX: isNaN(parsedDx),
        isNaNDY: isNaN(parsedDy),
        finalDx: point.dx,
        finalDy: point.dy,
        typeOfRawDX: typeof row.DX,
        typeOfRawDY: typeof row.DY
      });
    }
    
    return point;
  });

  // Chart options generator
  const createChartOptions = (title, xLabel, yLabel, aspectRatio = 1, forceXRange = null, forceYRange = null) => ({
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: aspectRatio,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const point = points[context.dataIndex];
            return [
              `Depth: ${point.depth.toFixed(1)} ft`,
              `${xLabel}: ${context.parsed.x.toFixed(1)}`,
              `${yLabel}: ${context.parsed.y.toFixed(1)}`,
              `Inclination: ${point.inclination.toFixed(1)}°`,
              `Azimuth: ${point.azimuth.toFixed(1)}°`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        display: true,
        title: {
          display: true,
          text: xLabel,
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.1)'
        },
        ...(forceXRange && {
          min: forceXRange.min,
          max: forceXRange.max
        })
      },
      y: {
        type: 'linear',
        display: true,
        title: {
          display: true,
          text: yLabel,
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.1)'
        },
        ...(forceYRange && {
          min: forceYRange.min,
          max: forceYRange.max
        })
      }
    },
    elements: {
      point: {
        radius: 3,
        hoverRadius: 6
      },
      line: {
        borderWidth: 2,
        tension: 0.1
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  });

  // Top View: DX vs DY (Plan View)
  const topViewChartData = points.map(p => ({ x: p.dx, y: p.dy }));
  
     const topViewData = {
     datasets: [
       {
         label: 'Well Path (Plan View)',
         data: topViewChartData,
         borderColor: 'rgb(75, 192, 192)',
         backgroundColor: 'rgba(75, 192, 192, 0.2)',
         pointBackgroundColor: points.map((p, i) => {
           // Color gradient based on depth
           const ratio = i / (points.length - 1);
           return `hsl(${240 - ratio * 120}, 70%, 50%)`;
         })
       }
     ]
   };

     // East View: DY vs TVD (East-West Section)
   const eastViewChartData = points.map(p => ({ x: p.dy, y: -p.tvd }));
   
   const eastViewData = {
     datasets: [
       {
         label: 'Well Path (East View)',
         data: eastViewChartData, // Negative TVD to show depth downward
         borderColor: 'rgb(255, 99, 132)',
         backgroundColor: 'rgba(255, 99, 132, 0.2)',
         pointBackgroundColor: points.map((p, i) => {
           const ratio = i / (points.length - 1);
           return `hsl(${240 - ratio * 120}, 70%, 50%)`;
         })
       }
     ]
   };

   // North View: DX vs TVD (North-South Section)
   const northViewChartData = points.map(p => ({ x: p.dx, y: -p.tvd }));
  
  const northViewData = {
    datasets: [
      {
        label: 'Well Path (North View)',
        data: northViewChartData, // Negative TVD to show depth downward
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.2)',
        pointBackgroundColor: points.map((p, i) => {
          const ratio = i / (points.length - 1);
          return `hsl(${240 - ratio * 120}, 70%, 50%)`;
        })
      }
    ]
  };

  // Calculate data ranges for explicit axis scaling
  const topViewXValues = topViewChartData.map(d => d.x);
  const topViewYValues = topViewChartData.map(d => d.y);
  const xRange = {
    min: Math.min(...topViewXValues) - Math.abs(Math.min(...topViewXValues)) * 0.1,
    max: Math.max(...topViewXValues) + Math.abs(Math.max(...topViewXValues)) * 0.1
  };
  const yRange = {
    min: Math.min(...topViewYValues) - Math.abs(Math.min(...topViewYValues)) * 0.1,
    max: Math.max(...topViewYValues) + Math.abs(Math.max(...topViewYValues)) * 0.1
  };
  
  console.log('🎯 Calculated axis ranges:', { xRange, yRange });
  
  // Calculate ranges for North View (DX vs TVD)
  const northViewXValues = northViewChartData.map(d => d.x); // DX values
  const northViewYValues = northViewChartData.map(d => d.y); // -TVD values
  const northXRange = {
    min: Math.min(...northViewXValues) - Math.abs(Math.min(...northViewXValues)) * 0.1,
    max: Math.max(...northViewXValues) + Math.abs(Math.max(...northViewXValues)) * 0.1
  };
  const northYRange = {
    min: Math.min(...northViewYValues) - Math.abs(Math.min(...northViewYValues)) * 0.1,
    max: Math.max(...northViewYValues) + Math.abs(Math.max(...northViewYValues)) * 0.1
  };
  
  // Calculate ranges for East View (DY vs TVD)
  const eastViewXValues = eastViewChartData.map(d => d.x); // DY values
  const eastViewYValues = eastViewChartData.map(d => d.y); // -TVD values
  const eastXRange = {
    min: Math.min(...eastViewXValues) - Math.abs(Math.min(...eastViewXValues)) * 0.1,
    max: Math.max(...eastViewXValues) + Math.abs(Math.max(...eastViewXValues)) * 0.1
  };
  const eastYRange = {
    min: Math.min(...eastViewYValues) - Math.abs(Math.min(...eastViewYValues)) * 0.1,
    max: Math.max(...eastViewYValues) + Math.abs(Math.max(...eastViewYValues)) * 0.1
  };
  
  console.log('🎯 North View axis ranges:', { northXRange, northYRange });
  console.log('🎯 East View axis ranges:', { eastXRange, eastYRange });
  
  const topViewOptions = createChartOptions('Top View (Plan)', 'Easting (DX) - ft', 'Northing (DY) - ft', 1, xRange, yRange);
  const eastViewOptions = createChartOptions('East View (Profile)', 'Northing (DY) - ft', 'Depth (TVD) - ft', 0.8, eastXRange, eastYRange);
  const northViewOptions = createChartOptions('North View (Profile)', 'Easting (DX) - ft', 'Depth (TVD) - ft', 0.8, northXRange, northYRange);
  
  // Debug chart options to see if there's a scale issue
  console.log('🎯 Chart options for Top View:', topViewOptions);
  
  // Let's also check if the data ranges are reasonable
  const xValues = topViewChartData.map(d => d.x);
  const yValues = topViewChartData.map(d => d.y);
  console.log('🎯 X-axis data range:', {
    min: Math.min(...xValues),
    max: Math.max(...xValues),
    values: xValues.slice(0, 5)
  });
  console.log('🎯 Y-axis data range:', {
    min: Math.min(...yValues),
    max: Math.max(...yValues),
    values: yValues.slice(0, 5)
  });

  return (
    <div className="well-path-2d-container">
      <div className="view-selector">
        <button 
          className={selectedView === 'all' ? 'active' : ''}
          onClick={() => setSelectedView('all')}
        >
          All Views
        </button>
        <button 
          className={selectedView === 'top' ? 'active' : ''}
          onClick={() => setSelectedView('top')}
        >
          Top View
        </button>
        <button 
          className={selectedView === 'east' ? 'active' : ''}
          onClick={() => setSelectedView('east')}
        >
          East View
        </button>
        <button 
          className={selectedView === 'north' ? 'active' : ''}
          onClick={() => setSelectedView('north')}
        >
          North View
        </button>
      </div>

      <div className="well-path-info">
        <div className="path-stats">
          <span><strong>Total Depth:</strong> {Math.max(...points.map(p => p.depth)).toFixed(1)} ft</span>
          <span><strong>TVD:</strong> {Math.max(...points.map(p => p.tvd)).toFixed(1)} ft</span>
          <span><strong>Max Displacement:</strong> {Math.sqrt(Math.max(...points.map(p => p.dx * p.dx + p.dy * p.dy))).toFixed(1)} ft</span>
          <span><strong>Survey Points:</strong> {points.length}</span>
        </div>
      </div>

             <div className={`charts-container ${selectedView}`}>
         {(selectedView === 'all' || selectedView === 'top') && (
           <div className="chart-wrapper">
             <Line data={topViewData} options={topViewOptions} />
           </div>
         )}
         
         {(selectedView === 'all' || selectedView === 'east') && (
           <div className="chart-wrapper">
             <Line data={eastViewData} options={eastViewOptions} />
           </div>
         )}
         
         {(selectedView === 'all' || selectedView === 'north') && (
           <div className="chart-wrapper">
             <Line data={northViewData} options={northViewOptions} />
           </div>
         )}
       </div>

      <div className="legend-info">
        <div className="color-legend">
          <span className="legend-item">
            <div className="color-box" style={{ backgroundColor: 'hsl(240, 70%, 50%)' }}></div>
            Surface
          </span>
          <span className="legend-item">
            <div className="color-box" style={{ backgroundColor: 'hsl(180, 70%, 50%)' }}></div>
            Mid-depth
          </span>
          <span className="legend-item">
            <div className="color-box" style={{ backgroundColor: 'hsl(120, 70%, 50%)' }}></div>
            TD
          </span>
        </div>
      </div>
    </div>
  );
} 